import { useCallback, useEffect, useState } from 'react';
import { api, type IgAutoReply, type Settings } from '../api.js';
import { PageHead } from './PageHead.js';

/**
 * Automação de respostas do Instagram — o "ManyChat" da casa.
 *
 * Comentou o gatilho num post publicado por aqui, recebe a resposta pública no
 * comentário e o link do produto na DM. A tela tem as três coisas que o dono
 * precisa: o interruptor, o texto que sai em nome dele, e o histórico que
 * prova que está funcionando.
 */

export function InboxAutomation() {
  const [settings, setSettings] = useState<Settings | null>(null);
  const [replies, setReplies] = useState<IgAutoReply[]>([]);
  const [salvando, setSalvando] = useState(false);
  const [aviso, setAviso] = useState<{ ok: boolean; texto: string } | null>(null);

  // Rascunho local dos campos de texto: salvar a cada tecla dispararia um
  // PATCH por caractere.
  const [gatilhos, setGatilhos] = useState('');
  /**
   * Vários textos de DM, em rotação.
   *
   * A mesma mensagem repetida dezenas de vezes por dia é gatilho de filtro de
   * spam do Instagram, com risco de bloqueio temporário de envios. Cada pessoa
   * recebe um texto diferente, alternando na ordem.
   */
  const [dms, setDms] = useState<string[]>(['']);
  const [comentario, setComentario] = useState('');

  const load = useCallback(async () => {
    const [s, r] = await Promise.all([
      api.settings(),
      api.igReplies().catch(() => [] as IgAutoReply[]),
    ]);
    setSettings(s);
    setReplies(r);
    setGatilhos((s.igAutoReplyTriggers ?? []).join(', '));
    const lista = Array.isArray(s.igAutoReplyDms) ? s.igAutoReplyDms : [];
    setDms(lista.length > 0 ? lista : ['']);
    setComentario(s.igAutoReplyComment ?? '');
  }, []);

  useEffect(() => {
    void load();
    // As DMs saem pelo worker; a tela se atualiza para o histórico acompanhar.
    const t = setInterval(() => {
      void api.igReplies().then(setReplies).catch(() => undefined);
    }, 30_000);
    return () => clearInterval(t);
  }, [load]);

  async function salvar(patch: Partial<Settings>) {
    setSalvando(true);
    setAviso(null);
    try {
      setSettings(await api.saveSettings(patch));
      setAviso({ ok: true, texto: 'Salvo.' });
    } catch (cause) {
      setAviso({
        ok: false,
        texto: cause instanceof Error ? cause.message : 'Falha ao salvar',
      });
    } finally {
      setSalvando(false);
    }
  }

  function salvarTextos() {
    const lista = gatilhos
      .split(',')
      .map((g) => g.trim())
      .filter((g) => g.length >= 2);
    void salvar({
      igAutoReplyTriggers: lista,
      igAutoReplyDms: dms.map((t) => t.trim()).filter((t) => t.length >= 10),
      igAutoReplyComment: comentario,
    });
  }

  if (!settings) {
    return (
      <div className="card empty">
        <span className="spinner" /> Carregando…
      </div>
    );
  }

  const ligada = settings.igAutoReplyEnabled ?? false;
  const enviadas = replies.filter((r) => r.dmSent).length;

  return (
    <>
      <PageHead
        eyebrow="Respostas · Instagram"
        title="Comentou, recebeu o link"
        description='Quem comentar o gatilho num post publicado por aqui recebe a resposta no comentário e o link do produto na DM — como se fosse você respondendo.'
        readout={[
          {
            label: 'automação',
            value: ligada ? 'ligada' : 'desligada',
            tom: ligada ? ('gain' as const) : ('quiet' as const),
          },
          { label: 'DMs enviadas', value: enviadas, tom: enviadas > 0 ? ('gain' as const) : undefined },
        ]}
        actions={
          <button
            type="button"
            className={ligada ? '' : 'primary'}
            disabled={salvando}
            onClick={() => void salvar({ igAutoReplyEnabled: !ligada })}
          >
            {salvando ? <span className="spinner" /> : ligada ? 'Desligar' : 'Ligar automação'}
          </button>
        }
      />

      {aviso && (
        <div className={`notice ${aviso.ok ? 'ok' : 'err'}`}>{aviso.texto}</div>
      )}

      <section className="card" style={{ padding: 18, marginBottom: 18 }}>
        <p className="section-title">O que dispara e o que sai</p>
        <div className="form-grid" style={{ gap: 14, maxWidth: 720 }}>
          <div>
            <label htmlFor="ar-gatilhos">Gatilhos (separados por vírgula)</label>
            <input
              id="ar-gatilhos"
              value={gatilhos}
              onChange={(e) => setGatilhos(e.target.value)}
              placeholder="eu quero, quero o link"
            />
            <p className="muted" style={{ fontSize: 12, margin: '5px 0 0' }}>
              Sem diferença de maiúsculas, acento ou emoji: “EU QUERO!!! 😍” conta.
            </p>
          </div>

          <div>
            <label htmlFor="ar-dm-0">Mensagens da DM (alternam entre si)</label>
            <p className="muted" style={{ fontSize: 12, margin: '0 0 8px' }}>
              A mesma mensagem repetida dezenas de vezes por dia é gatilho de filtro de
              spam do Instagram. Com vários textos, cada pessoa recebe um diferente e
              nenhum se repete em sequência.{' '}
              <span className="mono">{'{produto}'}</span> vira o nome do produto e{' '}
              <span className="mono">{'{link}'}</span> o link de afiliado daquele post.
            </p>

            {dms.map((texto, i) => (
              <div key={i} style={{ marginBottom: 8 }}>
                <div className="row" style={{ gap: 8, alignItems: 'baseline' }}>
                  <span className="mono" style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>
                    {i + 1}ª
                  </span>
                  <span style={{ flex: 1 }} />
                  {dms.length > 1 && (
                    <button
                      type="button"
                      className="queue-remove"
                      onClick={() => setDms(dms.filter((_, j) => j !== i))}
                      title="Remover este texto"
                    >
                      ✕
                    </button>
                  )}
                </div>
                <textarea
                  id={`ar-dm-${i}`}
                  value={texto}
                  onChange={(e) =>
                    setDms(dms.map((t, j) => (j === i ? e.target.value : t)))
                  }
                  style={{ minHeight: 84 }}
                />
              </div>
            ))}

            {/* Teto de 5: mais que isso é trabalho de escrita sem ganho — três
                já quebram a repetição que o filtro procura. */}
            {dms.length < 5 && (
              <button
                type="button"
                className="ghost"
                style={{ fontSize: 12.5 }}
                onClick={() => setDms([...dms, ''])}
              >
                + outro texto
              </button>
            )}
          </div>

          <div>
            <label htmlFor="ar-coment">Resposta pública no comentário</label>
            <input
              id="ar-coment"
              value={comentario}
              onChange={(e) => setComentario(e.target.value)}
              placeholder="deixe vazio para não responder no comentário"
            />
          </div>

          <button
            type="button"
            className="primary"
            style={{ maxWidth: 160 }}
            disabled={salvando}
            onClick={salvarTextos}
          >
            {salvando ? <span className="spinner" /> : 'Salvar textos'}
          </button>
        </div>
      </section>

      <section className="card" style={{ padding: 18 }}>
        <p className="section-title">
          Histórico{' '}
          <span className="muted" style={{ fontWeight: 400 }}>
            (últimos {replies.length})
          </span>
        </p>
        {replies.length === 0 ? (
          <p className="muted" style={{ margin: 0 }}>
            Nenhum comentário tratado ainda. A varredura passa a cada ~2 minutos nos
            posts dos últimos 5 dias publicados por aqui.
          </p>
        ) : (
          <div style={{ display: 'grid', gap: 8 }}>
            {replies.map((r) => (
              <div
                key={r.id}
                className="row"
                style={{
                  alignItems: 'baseline',
                  gap: 10,
                  padding: '8px 10px',
                  border: '1px solid var(--line)',
                  borderRadius: 'var(--r-control)',
                  fontSize: 12.5,
                }}
              >
                <strong style={{ flex: '0 0 auto' }}>@{r.username ?? '?'}</strong>
                <span className="muted" style={{ flex: 1, minWidth: 0 }}>
                  “{r.commentText ?? ''}”
                </span>
                {r.dmSent ? (
                  <span className="badge ok" style={{ flex: '0 0 auto' }}>
                    DM enviada
                  </span>
                ) : (
                  <span
                    className="badge err"
                    style={{ flex: '0 0 auto' }}
                    title={r.error ?? undefined}
                  >
                    {r.error ? 'falhou' : 'pendente'}
                  </span>
                )}
                <span className="muted" style={{ flex: '0 0 auto', fontSize: 11.5 }}>
                  {new Date(r.createdAt).toLocaleString('pt-BR', {
                    day: '2-digit',
                    month: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </span>
              </div>
            ))}
          </div>
        )}
      </section>
    </>
  );
}
